import "./trade.less";

import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { useConnection } from "@solana/wallet-adapter-react";
import type { Signer, TransactionInstruction } from "@solana/web3.js";
import { fromLamports, NATIVE_SOL, WRAPPED_SOL_MINT } from "@stepfinance/common";
import { TokenAccountParser } from "@stepfinance/tokens";
import BN from "bn.js";

import { findOrCreateAccountByMint, unwrapSol, wrapSol } from "../../actions";
import { confirmOptions } from "../../constants/transaction";
import { useAccountsContextRefresher } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useSwapContextState } from "../../contexts/swap";
import { useConnectedWallet } from "../../hooks/wallet/useConnectedWallet";
import { cache } from "../../utils/cache";
import { generateOneToOneNotificationDescription } from "../../utils/notifications";
import { getTokenName } from "../../utils/tokenInfo";
import { handlePreSendTxnError, sendTransaction } from "../../utils/transactions";
import { formatStringWithPlaceholders } from "../../utils/utils";
import { TradeButton } from ".";

type SwapProps = {
  pendingTx: boolean;
  setPendingTx: (val: boolean) => void;
  pendingConfirmation: boolean;
  setPendingConfirmation: (val: boolean) => void;
};

export const WrapUnwrapButton = (props: SwapProps) => {
  const { trackEvent } = useMatomo();
  const refreshAccounts = useAccountsContextRefresher();

  const wallet = useConnectedWallet();
  const { connected, watching } = useEnrichedWallet();

  const { connection } = useConnection();
  const { A, B } = useSwapContextState();

  const {
    currentLanguageData: {
      common: { notifications },
      pages: {
        swapLP: { swapButtonLabels },
      },
    },
  } = useLanguageContext();

  const { pendingTx, setPendingTx, pendingConfirmation, setPendingConfirmation } = props;

  const generateButtonLabel = (pendingTx: boolean, pendingConfirmation: boolean) => {
    if (pendingConfirmation) {
      return swapButtonLabels.awaitingTxn;
    } else if (pendingTx) {
      return swapButtonLabels.approveTxn;
    } else if (!connected) {
      return formatStringWithPlaceholders(
        swapButtonLabels.connectWalletToWrapOrUnwrap,
        A.mintAddress.equals(NATIVE_SOL) && B.mintAddress.equals(WRAPPED_SOL_MINT)
          ? swapButtonLabels.wrap.toLocaleLowerCase()
          : swapButtonLabels.unwrap.toLocaleLowerCase(),
      );
    } else if (!A.sufficientBalance()) {
      return formatStringWithPlaceholders(swapButtonLabels.insufficientBalance, A.name);
    } else if (A.mintAddress.equals(NATIVE_SOL)) {
      return swapButtonLabels.wrap;
    } else if (A.mintAddress.equals(WRAPPED_SOL_MINT)) {
      return swapButtonLabels.unwrap;
    }
    return swapButtonLabels.unknownAction;
  };

  const sendSwapTransaction = async () => {
    try {
      if (!wallet || pendingTx) {
        return;
      }
      if (!A.mint || !B.mint) {
        return;
      }
      setPendingTx(true);

      const signers: Signer[] = [];
      const createAccountInstructions: TransactionInstruction[] = [];
      const createCleanupInstructions: TransactionInstruction[] = [];
      const isWrapping = A.mintAddress.equals(NATIVE_SOL) && B.mintAddress.equals(WRAPPED_SOL_MINT);
      const amount = new BN(parseFloat(A.amount) * 10 ** A.mint.decimals);

      const wrappedSolKey = await findOrCreateAccountByMint(
        connection,
        wallet.publicKey,
        wallet.publicKey,
        createAccountInstructions,
        WRAPPED_SOL_MINT,
      );

      // Wrap the SOL into wrapped SOL.
      if (isWrapping) {
        await wrapSol(
          connection,
          createAccountInstructions,
          createCleanupInstructions,
          signers,
          wallet.publicKey,
          wrappedSolKey,
          new BN(amount),
        );
      } else {
        const wrappedSolAccount = await cache.query(connection, wrappedSolKey, TokenAccountParser);
        await unwrapSol(
          connection,
          createAccountInstructions,
          createCleanupInstructions,
          signers,
          wallet.publicKey,
          wrappedSolAccount.pubkey,
          wrappedSolAccount,
          amount,
        );
      }

      if (createAccountInstructions.length > 0) {
        const amountTransacted = fromLamports(amount, 9);

        await sendTransaction(
          connection,
          wallet,
          createAccountInstructions.concat(createCleanupInstructions),
          signers,
          true,
          confirmOptions,
          true,
          {
            approveMessage: notifications.approveTransaction,
            inProgress: {
              message: isWrapping
                ? formatStringWithPlaceholders(notifications.youAreDoingAction.message, "SOL")
                : formatStringWithPlaceholders(notifications.youAreDoingAction.message, "WSOL"),
              description: notifications.transactionInProgress,
            },
            success: {
              message: isWrapping
                ? formatStringWithPlaceholders(notifications.youDidAction.wrapped, "SOL")
                : formatStringWithPlaceholders(notifications.youDidAction.unwrapped, "WSOL"),
              description: generateOneToOneNotificationDescription({
                tokenA: {
                  action: isWrapping ? notifications.youDidAction.wrapped : notifications.youDidAction.unwrapped,
                  amount: amountTransacted,
                  mint: isWrapping ? NATIVE_SOL : WRAPPED_SOL_MINT,
                  type: "decrease",
                },
                tokenB: {
                  action: notifications.tokenBalanceChanges.received,
                  amount: amountTransacted,
                  mint: isWrapping ? WRAPPED_SOL_MINT : NATIVE_SOL,
                  type: "increase",
                },
              }),
            },
          },
          () => setPendingConfirmation(true),
        );
        refreshAccounts();
      }
    } catch (err) {
      handlePreSendTxnError(err as Error, notifications.wrapError.message);
    } finally {
      setPendingTx(false);
      setPendingConfirmation(false);

      trackEvent({
        category: "Swaps",
        action: "swap",
        name: `${getTokenName(A.mintAddress)}/${getTokenName(B.mintAddress)}`,
        value: parseFloat(A.amount),
      });
    }
  };

  const actionLabel = generateButtonLabel(pendingTx, pendingConfirmation);
  return (
    <>
      <TradeButton
        onClick={sendSwapTransaction}
        disabled={
          !connected ||
          (connected &&
            (watching ||
              pendingTx ||
              pendingConfirmation ||
              !A.account ||
              !B.mintAddress ||
              A.account === B.account ||
              !A.sufficientBalance())) ||
          Number(A.amount) === 0
        }
      >
        {actionLabel}
      </TradeButton>
    </>
  );
};
